import React, { useEffect, useState } from 'react'
import { Head, ModuleContainer, ProfileCard } from '../../../src/component'
import { getPrefetchRoutes, Route, usePostLoginRoute } from '../../../src/config/router'
import { TrackPageViewed } from '../../../src/integrations/monitoring'
import {
  checkoutErrors,
  CheckoutPage,
  CheckoutStatus,
  Features,
  PaymentMethodType,
  TerminalErrorCode,
  TopazStatus,
  WelcomeWithInitialLimitStatus,
} from '../../../src/model'
import { Cohort } from '@afterpaytouch/portal-api'
import {
  selectPurchaseMethod,
  setCheckoutStatus,
  useAppDispatch,
  useAppRouter,
  useCheckoutState,
  useCohort,
  useConsumerSelector,
  useCountry,
  usePreferredPaymentMethod,
  useShouldSetApplePayAsPurchaseMethod,
  usePreferredBankAccount,
  usePreferredCard,
  useRecoverableTerminalErrorsState,
  useShouldShowCashAppTooltip,
  useConsumerFeatureFlagReady,
  useConsumerLendingEligible,
  useShouldUseBankAccount,
  useIsBankAccountSelectionFailed,
  useTopazState,
  useSavedCards,
  handlePreferredFallback,
  updatePreferredCardWithError,
  updateSavedCardsWithError,
  useStartCheckout,
  setIsEnabledForMPANFlow,
  setCheckoutError,
  setLoggedInLoadingMessageKey,
  useShowLoaderOnly,
  useActiveCashAppPay,
  useOfferDetailPBI,
  useIsEligibleForAddPaymentMethodFlowExperiment,
  useExpandableLightweightPaymentPlans,
  useWelcomeWithInitialLimitStatus,
  useUkRegFlags,
} from '../../../src/state'
import { phoneNumberIsCleared } from '../../../src/utils/phone-numbers'
import { BankAccountDetails, RecoverableTerminalErrorStatus } from '@afterpay/types'
import { DesignLanguage, Heading, Loader, Text, useIsDesignLanguage, useToast } from '@afterpaytouch/core'
import { useFlag } from '@afterpaytouch/feature'
import { useCheckoutSizes } from '../../../src/utils/responsive'
import { useTranslation } from '../../utils/convergence'
import { isAfterpayInAppBrowser } from '../../utils/device'
import { getLoggedInLoadingMessages } from './helper'

export const LoggedInPage: CheckoutPage = () => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const { status, loggedInLoadingMessageKey } = useCheckoutState()
  const { hasFeatureFlagsReady, mobile, identityCheckStatus } = useConsumerSelector()
  const dispatch = useAppDispatch()
  const toast = useToast()
  const { textSize, headingSize } = useCheckoutSizes()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()
  const isBankAccountSelectionFailed = useIsBankAccountSelectionFailed()
  const preferredPaymentMethod = usePreferredPaymentMethod()
  const country = useCountry()
  const consumerFeatureFlagReady = useConsumerFeatureFlagReady()
  const { status: recoverableFlowStatus } = useRecoverableTerminalErrorsState()
  const postLoginRoute = usePostLoginRoute()
  const preferredBankAccount = usePreferredBankAccount()
  const preferredCard = usePreferredCard()
  const savedCards = useSavedCards()
  const cashAppPay = useActiveCashAppPay()
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const offerDetailPBI = useOfferDetailPBI()
  const isAfterpayInAppSession = isAfterpayInAppBrowser()
  const showLoaderOnly = useShowLoaderOnly()
  const pageTitle = `${t('common:brandName')}`

  const [requestCompleted, setStarted] = React.useState(false)
  const [checkAlternativePreferred, setCheckAlternativePreferred] = useState(false)
  const topazStatus = useTopazState()
  const { shouldOverrideCashAppPay } = useShouldShowCashAppTooltip()

  const isEnabledForApplePayMpanFlow = useFlag<boolean>(Features.ENABLE_MPAN_APPLE_PAY_FLOW, false)
  const { doStartCheckoutAndHandleError } = useStartCheckout()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)

  const isEligibleForAddPaymentMethodFlowExperiment = useIsEligibleForAddPaymentMethodFlowExperiment()
  const useExpandablePlans = useExpandableLightweightPaymentPlans()
  const welcomeWithInitialLimitStatus = useWelcomeWithInitialLimitStatus()
  const { isUkRegulationEnabled } = useUkRegFlags()
  const cohort = useCohort()

  const { title: loadingTitle, description: loadingDescription } = getLoggedInLoadingMessages(t, loggedInLoadingMessageKey ?? undefined)

  useEffect(() => {
    dispatch(setIsEnabledForMPANFlow(isEnabledForApplePayMpanFlow))
  }, [isEnabledForApplePayMpanFlow])

  useEffect(() => {
    const startCheckoutAndHandleError = async (): Promise<void> => {
      await doStartCheckoutAndHandleError()
      setLoggedInLoadingMessageKey(null)
      setStarted(true)
    }

    if (phoneNumberIsCleared(mobile)) {
      router.replace(Route.ADD_MOBILE)
    } else if (
      status === CheckoutStatus.Loaded ||
      status === CheckoutStatus.Closed ||
      status === CheckoutStatus.IdentityIneligible ||
      recoverableFlowStatus === RecoverableTerminalErrorStatus.Finished
    ) {
      if (consumerFeatureFlagReady) {
        startCheckoutAndHandleError()
      }
    }
  }, [consumerFeatureFlagReady, recoverableFlowStatus, identityCheckStatus])

  useEffect(() => {
    // if the logged in page get refreshed and the local state does not match started, update started local state
    if (status === CheckoutStatus.Started) {
      setStarted(true)
    }
  }, [status])

  useEffect(() => {
    // check if fallback is available
    const fallback = savedCards.filter((card) => card.error === null).pop() ?? null
    if (checkAlternativePreferred && fallback != null) {
      // do checks on next available valid card
      dispatch(handlePreferredFallback(fallback, router.replace(postLoginRoute)))
    } else if (checkAlternativePreferred && fallback === null) {
      // if no available fallback, preferred card stays the same as the original and error is set before navigating to summary
      dispatch(updatePreferredCardWithError(preferredCard, savedCards))
      router.replace(postLoginRoute)
    }
  }, [checkAlternativePreferred, savedCards])

  useEffect(() => {
    if (status === CheckoutStatus.Started && requestCompleted && hasFeatureFlagsReady && !checkAlternativePreferred) {
      if (welcomeWithInitialLimitStatus === WelcomeWithInitialLimitStatus.Eligible && isUkRegulationEnabled) {
        router.replace(Route.WELCOME_WITH_INITIAL_LIMIT)
        return
      }

      if (cashAppPay.length > 0 && (preferredPaymentMethod === PaymentMethodType.CASH_APP_PAY || shouldOverrideCashAppPay)) {
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.CASH_APP_PAY, cashAppPay: cashAppPay[0] }, false))
      } else if (shouldUseApplePay) {
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.APPLE_PAY }))
      } else if (shouldUseBankAccount) {
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.ACH_BANK_ACCOUNT, account: preferredBankAccount?.account as BankAccountDetails }, false))
      } else if (preferredPaymentMethod === PaymentMethodType.CREDIT_CARD && preferredCard?.card !== null) {
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.CREDIT_CARD, card: { token: preferredCard?.card?.token as string } }))
      } else if (cashAppPay.length > 0) {
        // fallback to cashapp
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.CASH_APP_PAY, cashAppPay: cashAppPay[0] }, false))
      } else if (preferredCard?.card !== null) {
        // fallback to card
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.CREDIT_CARD, card: { token: preferredCard?.card?.token as string } }))
      } else if (!isConsumerLendingEligible) {
        if (cohort === Cohort.CashOnly) {
          dispatch(setCheckoutError({ error: TerminalErrorCode.Default }))
          router.replace(Route.TERMINAL_ERROR)
        } else if (isEligibleForAddPaymentMethodFlowExperiment) {
          router.replace(postLoginRoute)
        } else {
          dispatch(setCheckoutStatus(CheckoutStatus.LackOfEligiblePaymentMethod))
          router.replace(Route.ADD_PAYMENT_METHOD)
        }
        return
      }

      if (isBankAccountSelectionFailed) {
        toast({
          message: () => (
            <>
              <Text size={textSize} bold>
                {t('summary:bankAccountErrors:fraudCheckNotAllowed:heading', { lastFour: preferredBankAccount?.account?.numberMask })}
              </Text>
              <Text size={textSize}>{t('summary:bankAccountErrors:fraudCheckNotAllowed:message')}</Text>
            </>
          ),
          kind: 'Default',
          toastTime: 5000,
        })
      }

      if (preferredPaymentMethod !== PaymentMethodType.CREDIT_CARD || isConsumerLendingEligible) {
        router.replace(postLoginRoute)
      }
    }
  }, [status, requestCompleted, hasFeatureFlagsReady, welcomeWithInitialLimitStatus, isUkRegulationEnabled])

  useEffect(() => {
    if (topazStatus.purchase.status > TopazStatus.Saving && !checkAlternativePreferred) {
      if (topazStatus.purchase.status === TopazStatus.Saved || preferredPaymentMethod !== PaymentMethodType.CREDIT_CARD) {
        router.replace(postLoginRoute)
      } else {
        dispatch(updateSavedCardsWithError(topazStatus.purchase.status, preferredCard?.card?.token, savedCards))
        setCheckAlternativePreferred(true)
      }
    }
  }, [topazStatus.purchase.status])

  const verifyRoutes = getPrefetchRoutes(country)

  useEffect(() => {
    router.prefetch(Route.ADD_PAYMENT_METHOD)
    router.prefetch(Route.ADD_MOBILE)
    router.prefetch(Route.TERMINAL_ERROR)
    router.prefetch(Route.SUMMARY)
    if (useExpandablePlans) {
      router.prefetch(Route.PAYMENT_OPTIONS)
    } else {
      router.prefetch(Route.PAYMENT_TYPES)
    }
    router.prefetch(Route.PAY_MONTHLY_ONLY)
    router.prefetch(Route.WELCOME_WITH_INITIAL_LIMIT)
    verifyRoutes.map((url) => router.prefetch(url))
  }, [verifyRoutes, useExpandablePlans])

  useEffect(() => {
    if (offerDetailPBI?.rejectedReasonCode && !isConsumerLendingEligible) {
      dispatch(setCheckoutError({ error: checkoutErrors[offerDetailPBI?.rejectedReasonCode] ?? TerminalErrorCode.Default }))
      router.replace(Route.TERMINAL_ERROR)
    }
  }, [offerDetailPBI?.rejectedReasonCode, isConsumerLendingEligible])

  if (isArcadeDesignLanguage) {
    return (
      <>
        <Loader alignCenter withHeader text={loggedInLoadingMessageKey ? loadingDescription : undefined} />
        <TrackPageViewed />
      </>
    )
  }

  return (
    <>
      <Head title={pageTitle} />
      {isAfterpayInAppSession ? (
        <Loader alignCenter withHeader={!showLoaderOnly} />
      ) : (
        <>
          {loggedInLoadingMessageKey && <Heading size={headingSize}>{loadingTitle}</Heading>}
          <ModuleContainer padding={{ vertical: 'Large', horizontal: 'XLarge' }}>
            <ProfileCard />
            <Loader text={loadingDescription} />
          </ModuleContainer>
        </>
      )}
      <TrackPageViewed eventProperties={{ isEnabledForApplePayMpanFlow, loggedInLoadingMessageKey }} />
    </>
  )
}
